import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getWorkloadHeatmap, type WorkloadData, type AssigneeInfo, type HeatmapCell, type AssignedTask } from '../../packages/api-client/entities-api';
import { formatDueDate } from '../../utils/date';
import { usePageTitle } from '../../hooks/usePageTitle';
import { PageHeader, StateView } from '../../packages/ui';
import { ClipboardList, Calendar } from 'lucide-react';
import { PriorityIcon } from '../../components/taskIcons';
import { statusColor, priorityColor, priorityTint, varAlpha } from '../../utils/statusColors';

function addDays(d: Date, n: number) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function toISO(d: Date) { return d.toISOString().split('T')[0]; }
function formatMonth(d: Date) { return d.toLocaleDateString('en', { month: 'short', year: 'numeric' }); }
function formatDay(d: Date) { return d.toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric' }); }

function cellColor(count: number, max: number): string {
    if (count === 0) return 'var(--color-surface-2)';
    const pct = max > 0 ? count / max : 0;
    if (pct < 0.25) return varAlpha('accent', 0.25);
    if (pct < 0.44) return varAlpha('accent', 0.44);
    if (pct < 0.67) return varAlpha('accent', 0.67);
    return 'var(--accent)';
}

function initials(a: AssigneeInfo): string {
    return ((a.first_name?.[0] || '') + (a.last_name?.[0] || '') || a.email?.[0] || '?').toUpperCase();
}

export function WorkloadPage() {
    usePageTitle('Workload');
    const now = new Date();
    // Default: 30 days ago → 30 days ahead so both past and upcoming tasks show
    const [from, setFrom] = useState(toISO(addDays(now, -30)));
    const [to, setTo] = useState(toISO(addDays(now, 30)));
    const [data, setData] = useState<WorkloadData | null>(null);
    const [loading, setLoading] = useState(true);
    const [tooltip, setTooltip] = useState<{ cell: HeatmapCell; x: number; y: number } | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        getWorkloadHeatmap(from, to)
            .then(d => {
                setData(d ? {
                    ...d,
                    assignees: d.assignees ?? [],
                    cells: d.cells ?? [],
                    tasks: d.tasks ?? [],
                } : null);
                setLoading(false);
            })
            .catch(err => {
                console.error('Workload error:', err);
                setError('Workload could not be loaded. Check your connection and retry.');
                setLoading(false);
            });
    }, [from, to]);
    useEffect(load, [load]);

    const days = useMemo(() => {
        if (!from || !to) return [];
        const result: string[] = [];
        let d = new Date(from);
        const end = new Date(to);
        while (d <= end) { result.push(toISO(d)); d = addDays(d, 1); }
        return result;
    }, [from, to]);

    const cellMap = useMemo(() => {
        const map: Record<string, Record<string, number>> = {};
        (data?.cells ?? []).forEach(c => {
            if (!map[c.assignee_id]) map[c.assignee_id] = {};
            map[c.assignee_id][c.date] = c.count;
        });
        return map;
    }, [data]);

    const maxCount = useMemo(() => {
        if (!data?.cells?.length) return 1;
        return Math.max(1, ...data.cells.map(c => c.count));
    }, [data]);

    const monthLabels = useMemo(() => {
        const labels: { label: string; startIdx: number }[] = [];
        let lastMonth = '';
        days.forEach((d, i) => {
            const mo = d.slice(0, 7);
            if (mo !== lastMonth) { labels.push({ label: formatMonth(new Date(d)), startIdx: i }); lastMonth = mo; }
        });
        return labels;
    }, [days]);

    // Group tasks by assignee
    const tasksByAssignee = useMemo(() => {
        const map: Record<string, AssignedTask[]> = {};
        (data?.tasks ?? []).forEach(t => {
            if (!map[t.assignee_id]) map[t.assignee_id] = [];
            map[t.assignee_id].push(t);
        });
        return map;
    }, [data]);

    const assigneeMap = useMemo(() => {
        const map: Record<string, AssigneeInfo> = {};
        (data?.assignees ?? []).forEach(a => { map[a.id] = a; });
        return map;
    }, [data]);

    const CELL = 18;
    const GAP = 2;

    return (
        <div>
            <PageHeader
                title="Workload"
                subtitle="Assigned tasks by team member. Measured in task counts, not hours or capacity."
                actions={
                    <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap', fontSize: 'var(--font-size-sm)' }}>
                        <label style={{ display: 'contents' }}>
                            <span style={{ color: 'var(--color-text-muted)' }}>From</span>
                            <input type="date" className="form-input" style={{ height: 36, width: 150 }} value={from} onChange={e => setFrom(e.target.value)} max={to} />
                        </label>
                        <label style={{ display: 'contents' }}>
                            <span style={{ color: 'var(--color-text-muted)' }}>To</span>
                            <input type="date" className="form-input" style={{ height: 36, width: 150 }} value={to} onChange={e => setTo(e.target.value)} min={from} />
                        </label>
                    </div>
                }
            />

            {loading ? (
                <StateView state="loading" label="Loading workload" lines={6} />
            ) : error ? (
                <StateView state="error" message={error} onRetry={load} />
            ) : (
                <>
                    {/* ─── Assigned Tasks List ─────────────────────────────── */}
                    <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
                        <div style={{ fontWeight: 700, marginBottom: 'var(--space-4)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <ClipboardList size={18} aria-hidden="true" /> Open assigned tasks
                            <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                                ({(data?.tasks ?? []).length} task{(data?.tasks ?? []).length !== 1 ? 's' : ''}, every date)
                            </span>
                        </div>

                        {Object.keys(tasksByAssignee).length === 0 ? (
                            <StateView state="empty" title="No assigned open tasks" message="Assign a task to a team member to see it here." />
                        ) : (
                            Object.entries(tasksByAssignee).map(([aid, tasks]) => {
                                const a = assigneeMap[aid];
                                const displayName = a
                                    ? (`${a.first_name || ''} ${a.last_name || ''}`).trim() || a.email
                                    : aid.slice(0, 8);
                                const avatarLetters = a ? initials(a) : aid[0].toUpperCase();
                                return (
                                    <div key={aid} style={{ marginBottom: '1.5rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem' }}>
                                            <div style={{
                                                width: '2rem', height: '2rem', borderRadius: '50%',
                                                background: 'var(--color-primary-glow)', color: 'var(--color-primary)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontWeight: 700, fontSize: '0.75rem', flexShrink: 0,
                                            }}>
                                                {avatarLetters}
                                            </div>
                                            <span style={{ fontWeight: 600 }}>{displayName}</span>
                                            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                                                {tasks.length} task{tasks.length !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', paddingLeft: '2.6rem' }}>
                                            {tasks.map(t => (
                                                <div key={t.id} style={{
                                                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                                                    padding: '0.5rem 0.75rem', borderRadius: 'var(--radius-md)',
                                                    background: 'var(--color-surface-2)', fontSize: 'var(--font-size-sm)',
                                                }}>
                                                    <span style={{
                                                        width: 8, height: 8, borderRadius: '50%',
                                                        background: statusColor(t.status), flexShrink: 0,
                                                    }} />
                                                    <span style={{ flex: 1 }}>{t.title}</span>
                                                    <span style={{
                                                        fontSize: 'var(--font-size-xs)',
                                                        background: priorityTint(t.priority, 0.13),
                                                        color: priorityColor(t.priority),
                                                        padding: '1px 7px', borderRadius: 99, textTransform: 'capitalize', flexShrink: 0,
                                                        display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                                                    }}>
                                                        <PriorityIcon priority={t.priority} /> {t.priority}
                                                    </span>
                                                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', flexShrink: 0 }}>
                                                        {t.due_date
                                                            ? `Due ${formatDueDate(t.due_date, { month: 'short', day: 'numeric' })}`
                                                            : 'No due date'}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* ─── Heatmap ─────────────────────────────────────────── */}
                    {!data || data.assignees.length === 0 ? (
                        <StateView state="empty" title="No dated tasks in this range"
                            message="The heatmap counts assigned tasks by due date (or created date when there is none). Widen the range or assign tasks to see it." />
                    ) : (
                        <div className="card" style={{ overflow: 'auto' }}>
                            <div style={{ fontWeight: 700, marginBottom: 'var(--space-1)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Calendar size={18} aria-hidden="true" /> Tasks per day</div>
                            <p style={{ margin: '0 0 var(--space-4)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                                Number of assigned tasks, any status, on their due date (or created date when there is none). A count, not hours.
                            </p>
                            {/* Legend */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-5)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                                <span>Less</span>
                                {([
                                    'var(--track)',
                                    varAlpha('accent', 0.25),
                                    varAlpha('accent', 0.44),
                                    varAlpha('accent', 0.67),
                                    'var(--accent)',
                                ] as const).map((bg, i) => (
                                    <div key={i} style={{ width: CELL, height: CELL, borderRadius: 3, background: bg }} />
                                ))}
                                <span>More</span>
                            </div>
                            {/* Month labels */}
                            <div style={{ display: 'flex', marginLeft: 140, marginBottom: 4 }}>
                                {monthLabels.map((ml, i) => (
                                    <div key={i} style={{ position: 'relative', left: ml.startIdx * (CELL + GAP), fontSize: '0.6rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap', marginRight: 'auto' }}>
                                        {ml.label}
                                    </div>
                                ))}
                            </div>
                            {/* Heatmap rows */}
                            {data.assignees.map(assignee => (
                                <div key={assignee.id} style={{ display: 'flex', alignItems: 'center', marginBottom: GAP * 2 }}>
                                    <div style={{ width: 132, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 'var(--space-2)', paddingRight: 'var(--space-3)' }}>
                                        <div style={{ width: '1.5rem', height: '1.5rem', borderRadius: '50%', background: 'var(--color-primary-glow)', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700, flexShrink: 0 }}>
                                            {initials(assignee)}
                                        </div>
                                        <span style={{ fontSize: '0.65rem', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {assignee.first_name || assignee.email.split('@')[0]}
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', gap: GAP }}>
                                        {days.map(day => {
                                            const count = cellMap[assignee.id]?.[day] ?? 0;
                                            return (
                                                <div
                                                    key={day}
                                                    style={{ width: CELL, height: CELL, borderRadius: 3, background: cellColor(count, maxCount), cursor: count > 0 ? 'pointer' : 'default', flexShrink: 0, transition: 'opacity 0.1s' }}
                                                    onMouseEnter={e => { if (count > 0) setTooltip({ cell: { assignee_id: assignee.id, date: day, count }, x: e.clientX, y: e.clientY }); }}
                                                    onMouseLeave={() => setTooltip(null)}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* Tooltip */}
            {tooltip && (
                <div style={{ position: 'fixed', top: tooltip.y - 50, left: tooltip.x + 12, background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '8px 12px', fontSize: 'var(--font-size-xs)', pointerEvents: 'none', zIndex: 9999, boxShadow: 'var(--shadow-md)', whiteSpace: 'nowrap' }}>
                    <div style={{ fontWeight: 600 }}>{formatDay(new Date(tooltip.cell.date))}</div>
                    <div style={{ color: 'var(--color-primary)' }}>{tooltip.cell.count} task{tooltip.cell.count !== 1 ? 's' : ''}</div>
                </div>
            )}
        </div>
    );
}
